/**
 * Entity Animation System
 *
 * Runtime playback engine for keyframe animations of scene entities (transforms, lights,
 * particles, audio, opacity, visibility) and scene-wide targets (fog, weather, focus/haze
 * regions, post effects, relighting). Supports time-based (independent) and scroll-linked
 * animations. The animatable properties of each target type live in animationProperties.ts.
 *
 * Transform keyframes are stored in BabylonJS conventions and converted to PlayCanvas at
 * apply-time (negate Z, radians→degrees for rotation). Everything else is stored in the units
 * the editor shows.
 *
 * Playback semantics (mirrored in the Animation docs):
 * - An enabled time-based animation holds its first-keyframe pose from the moment the scene is
 *   presented: during its delay, and until it is started (no autoplay), exactly like the editor
 *   timeline at 0s.
 * - autoplay starts it on load. triggerWaypoint restarts it each time the tour reaches that
 *   waypoint (and it then never autoplays). Linked actions and the public API play, pause,
 *   restart and seek it.
 * - 'once' holds the final pose and emits a 'complete' playback event; 'loop' wraps;
 *   'pingpong' reverses at either end.
 */
import * as pc from 'playcanvas';
import type { EntityAnimationConfig, AnimationTrackConfig } from '../types';
import { applyAnimationEasing, isSceneAnimationTarget, isStepAnimationProperty } from './animationProperties';
import { forwardRotationFromLight, lightRotationFromForward } from '../effects/shadows/lightDirection';

const _lightForward = new pc.Quat();
/**
 * Rotation keyframes describe a light by its forward axis (where it points), as the editor always
 * has; the light entity itself is turned so the engine's -Y lighting axis lands on that forward
 * (effects/shadows/lightDirection). Read and write lights through that mapping.
 */
function readAnimatedEuler(entity: pc.Entity, entityType: string): pc.Vec3 {
    if (entityType !== 'light') return entity.getLocalEulerAngles().clone();
    return forwardRotationFromLight(entity.getLocalRotation(), _lightForward).getEulerAngles();
}
function writeAnimatedEuler(entity: pc.Entity, entityType: string, euler: pc.Vec3): void {
    if (entityType !== 'light') {
        entity.setLocalEulerAngles(euler);
        return;
    }
    _lightForward.setFromEulerAngles(euler.x, euler.y, euler.z);
    entity.setLocalRotation(lightRotationFromForward(_lightForward, _lightForward));
}

type EntityLookup = (entityId: string, entityType: string) => pc.Entity | null;

/**
 * Scene-wide target that does not map to a pc.Entity (fog, weather, focus/haze regions, post
 * effects, relighting). `readValues` backs the editor's "capture keyframe" flow.
 */
export interface SceneAnimationHandler {
    apply(values: Map<string, number>, entityId: string): void;
    readValues(entityId: string): Record<string, number> | null;
    /** These properties stopped being animated (track, animation removed or disabled): restore their configured values. */
    reset?(properties: string[], entityId: string): void;
}
export interface SceneAnimationHandlers {
    [entityType: string]: SceneAnimationHandler | undefined;
}

/**
 * Writers for entity properties whose look is owned by other viewer systems: hotspot and model
 * opacity go through the splat-depth-aware translucency path, and visibility has to coexist
 * with visibility ranges. Without a handler, `visible` toggles entity.enabled and `opacity`
 * is ignored.
 */
export interface EntityPresentationHandlers {
    setOpacity?(entity: pc.Entity, entityType: string, entityId: string, opacity: number): void;
    getOpacity?(entity: pc.Entity, entityType: string, entityId: string): number | null;
    setVisible?(entity: pc.Entity, entityType: string, entityId: string, visible: boolean): void;
    getVisible?(entity: pc.Entity, entityType: string, entityId: string): boolean | null;
    /**
     * These properties stopped being animated: restore their configured values (visibility,
     * opacity, light and audio settings). Transforms are left where they are; the editor owns them.
     */
    resetProperties?(entity: pc.Entity, entityType: string, entityId: string, properties: string[]): void;
    /** Viewer-owned properties (marker color, mirror and water looks): apply the ones present in `values`. */
    setProperties?(entity: pc.Entity, entityType: string, entityId: string, values: Map<string, number>): void;
    /** Current values of the viewer-owned properties, for capture. */
    getProperties?(entity: pc.Entity, entityType: string, entityId: string): Record<string, number> | null;
}

/** Authored particle values the multipliers apply to (captured before the first animated write). */
interface ParticleBase {
    rate: number;
    rate2: number;
    lifetime: number;
    alpha: number[][] | null;
    color: number[][][] | null;
    opacity: number;
    tint: [number, number, number];
}

export interface EntityAnimationPlaybackEvent {
    /** 'start': began advancing from its start. 'complete': a 'once' animation reached its end. */
    type: 'start' | 'complete';
    id: string;
    name: string;
    entityId: string;
    entityType: string;
}

export interface EntityAnimationSystemOptions {
    presentation?: EntityPresentationHandlers;
    onPlaybackEvent?: (event: EntityAnimationPlaybackEvent) => void;
}

/** Public metadata for viewer.getEntityAnimations(). */
export interface EntityAnimationInfo {
    id: string;
    name: string;
    entityId: string;
    entityType: string;
    timelineMode: string;
    duration: number;
    playbackMode: string;
    autoplay: boolean;
    triggerWaypoint?: number;
    playing: boolean;
}

/**
 * Interpolate a track at a given time using binary search + easing
 */
function evaluateTrack(track: AnimationTrackConfig, time: number): number | null {
    const kfs = track.keyframes;
    if (kfs.length === 0) return null;
    if (kfs.length === 1) return kfs[0].value;

    // Before first keyframe
    if (time <= kfs[0].time) return kfs[0].value;
    // After last keyframe
    if (time >= kfs[kfs.length - 1].time) return kfs[kfs.length - 1].value;

    // Binary search for the interval
    let lo = 0;
    let hi = kfs.length - 1;
    while (lo < hi - 1) {
        const mid = (lo + hi) >> 1;
        if (kfs[mid].time <= time) {
            lo = mid;
        } else {
            hi = mid;
        }
    }

    const k0 = kfs[lo];
    const k1 = kfs[hi];
    const segmentDuration = k1.time - k0.time;
    if (segmentDuration <= 0) return k0.value;

    // On/off switches hold whatever their easing says (a linear "visible" would flip halfway).
    if (isStepAnimationProperty(track.property)) return k0.value;

    const localT = (time - k0.time) / segmentDuration;
    const easedT = applyAnimationEasing(localT, k0.easing);
    return k0.value + (k1.value - k0.value) * easedT;
}

interface AnimationState {
    time: number;
    delayRemaining: number;
    direction: 1 | -1; // for pingpong
    playing: boolean;
    /** The start pose has been applied once (delay / not started yet). */
    primed: boolean;
    /** A 'start' event was emitted for the current run. */
    started: boolean;
}

const TRANSFORM_POSITION = ['position.x', 'position.y', 'position.z'];
const TRANSFORM_ROTATION = ['rotation.x', 'rotation.y', 'rotation.z'];
const TRANSFORM_SCALE = ['scale.x', 'scale.y', 'scale.z'];
const OPACITY_TYPES: ReadonlySet<string> = new Set(['hotspot', 'portal', 'customMesh']);

function hasAny(values: Map<string, number>, keys: string[]): boolean {
    return values.has(keys[0]) || values.has(keys[1]) || values.has(keys[2]);
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function hasWaypointTrigger(anim: EntityAnimationConfig): boolean {
    return typeof anim.triggerWaypoint === 'number' && Number.isInteger(anim.triggerWaypoint) && anim.triggerWaypoint >= 0;
}

function initialState(anim: EntityAnimationConfig): AnimationState {
    return {
        time: 0,
        delayRemaining: anim.delay || 0,
        direction: 1,
        playing: anim.autoplay && anim.enabled && !hasWaypointTrigger(anim),
        primed: false,
        started: false,
    };
}

export class EntityAnimationSystem {
    private animations: EntityAnimationConfig[];
    private entityLookup: EntityLookup;
    private sceneHandlers: SceneAnimationHandlers;
    private presentation: EntityPresentationHandlers;
    private onPlaybackEvent?: (event: EntityAnimationPlaybackEvent) => void;
    private states: Map<string, AnimationState> = new Map();
    private destroyed = false;
    private warnedEntities = new Set<string>();
    private curveCache = new Map<string, pc.Curve>();
    private curveSetCache = new Map<string, pc.CurveSet>();
    private coneRatios = new WeakMap<object, number>();
    private particleBases = new WeakMap<object, ParticleBase>();
    private scratchValues = new Map<string, number>();
    /** Properties written per target, so removing a track can restore what it changed. */
    private appliedProps = new Map<string, { entityType: string; entityId: string; props: Set<string> }>();
    private scratchColor = new pc.Color();

    constructor(entityLookup: EntityLookup, animations: EntityAnimationConfig[], sceneHandlers: SceneAnimationHandlers = {}, options: EntityAnimationSystemOptions = {}) {
        this.entityLookup = entityLookup;
        this.sceneHandlers = sceneHandlers;
        this.presentation = options.presentation ?? {};
        this.onPlaybackEvent = options.onPlaybackEvent;
        this.animations = animations;

        // Initialize state for each independent animation
        for (const anim of animations) {
            if (anim.timelineMode === 'independent') {
                this.states.set(anim.id, initialState(anim));
            }
        }
    }

    private emit(type: EntityAnimationPlaybackEvent['type'], anim: EntityAnimationConfig): void {
        if (!this.onPlaybackEvent) return;
        try {
            this.onPlaybackEvent({ type, id: anim.id, name: anim.name, entityId: anim.entityId, entityType: anim.entityType });
        } catch (err) {
            console.warn('[EntityAnim] playback event listener failed:', err);
        }
    }

    /**
     * Called every frame from the app update loop.
     * @param dt - Delta time in seconds
     * @param scrollProgress - Current scroll progress 0-1 (for scroll-linked animations)
     */
    update(dt: number, scrollProgress: number): void {
        if (this.destroyed) return;

        for (const anim of this.animations) {
            if (!anim.enabled) continue;

            if (anim.timelineMode === 'scroll') {
                // Scroll-linked: map scrollProgress (0-1) to 0-100
                const scrollPercent = scrollProgress * 100;
                this.evaluateAndApply(anim, scrollPercent);
            } else {
                // Independent: advance time
                if (!(anim.duration > 0)) continue; // guard against 0/negative duration (would produce NaN in loop mode)
                const state = this.states.get(anim.id);
                if (!state) continue;

                // Hold the start pose while waiting (delay, or not started yet) so the scene
                // matches the editor timeline at 0s instead of showing the entity's base pose.
                // Targets created later (hotspots and models load after the splat) are retried
                // every frame until they exist.
                if (!state.primed) {
                    state.primed = state.playing && state.delayRemaining <= 0
                        ? true
                        : this.evaluateAndApply(anim, state.time);
                }
                if (!state.playing) continue;

                // Handle delay
                if (state.delayRemaining > 0) {
                    state.delayRemaining -= dt;
                    continue;
                }

                if (!state.started) {
                    state.started = true;
                    this.emit('start', anim);
                }

                state.time += dt * state.direction;

                // Handle end-of-animation based on playback mode
                if (state.time >= anim.duration) {
                    switch (anim.playbackMode) {
                        case 'loop':
                            state.time = state.time % anim.duration;
                            break;
                        case 'pingpong': {
                            const overshoot = state.time - anim.duration;
                            state.time = Math.max(0, anim.duration - overshoot);
                            state.direction = -1;
                            break;
                        }
                        case 'once':
                        default:
                            state.time = anim.duration;
                            state.playing = false;
                            this.evaluateAndApply(anim, state.time);
                            this.emit('complete', anim);
                            continue;
                    }
                } else if (state.time <= 0 && state.direction === -1) {
                    // Pingpong reverse hit start — preserve overshoot, clamp to duration
                    state.time = Math.min(anim.duration, Math.abs(state.time));
                    state.direction = 1;
                }

                this.evaluateAndApply(anim, state.time);
            }
        }
    }

    /**
     * Evaluate all tracks of an animation at a given time and apply to entity.
     * @returns false when the target does not exist (yet), so a held pose can be retried.
     */
    private evaluateAndApply(anim: EntityAnimationConfig, time: number): boolean {
        const values = this.scratchValues;
        values.clear();

        for (const track of anim.tracks) {
            const value = evaluateTrack(track, time);
            if (value !== null && Number.isFinite(value)) {
                values.set(track.property, value);
            }
        }

        if (values.size === 0) return true;

        // Scene-wide targets have no pc.Entity
        if (isSceneAnimationTarget(anim.entityType)) {
            const handler = this.sceneHandlers[anim.entityType];
            if (handler) {
                handler.apply(values, anim.entityId);
                this.noteApplied(anim, values);
            }
            return true;
        }

        const entity = this.entityLookup(anim.entityId, anim.entityType);
        if (!entity) {
            const warnKey = `${anim.entityId}:${anim.entityType}`;
            if (!this.warnedEntities.has(warnKey)) {
                this.warnedEntities.add(warnKey);
                console.warn(`[EntityAnim] Entity not found (yet): id="${anim.entityId}", type="${anim.entityType}"`);
            }
            return false;
        }
        this.applyValues(entity, values, anim);
        this.noteApplied(anim, values);
        return true;
    }

    private noteApplied(anim: EntityAnimationConfig, values: Map<string, number>): void {
        const key = `${anim.entityType}\u0000${anim.entityId}`;
        let entry = this.appliedProps.get(key);
        if (!entry) {
            entry = { entityType: anim.entityType, entityId: anim.entityId, props: new Set() };
            this.appliedProps.set(key, entry);
        }
        for (const prop of values.keys()) entry.props.add(prop);
    }

    /** Restore the configured values of properties no enabled animation drives any more. */
    private restoreUnanimated(animations: EntityAnimationConfig[]): void {
        if (this.appliedProps.size === 0) return;
        const live = new Map<string, Set<string>>();
        for (const anim of animations) {
            if (!anim.enabled) continue;
            const key = `${anim.entityType}\u0000${anim.entityId}`;
            let props = live.get(key);
            if (!props) live.set(key, props = new Set());
            for (const track of anim.tracks) if (track.keyframes.length > 0) props.add(track.property);
        }
        for (const [key, entry] of this.appliedProps) {
            const still = live.get(key);
            const removed = [...entry.props].filter((p) => !still?.has(p));
            if (removed.length === 0) continue;
            try {
                if (isSceneAnimationTarget(entry.entityType)) {
                    this.sceneHandlers[entry.entityType]?.reset?.(removed, entry.entityId);
                } else {
                    const entity = this.entityLookup(entry.entityId, entry.entityType);
                    if (entity) {
                        const ps = (entity as any).particlesystem;
                        if (ps) this.restoreParticle(ps, removed);
                        if (this.presentation.resetProperties) this.presentation.resetProperties(entity, entry.entityType, entry.entityId, removed);
                        else if (removed.includes('visible') && !entity.enabled) entity.enabled = true;
                    }
                }
            } catch (err) {
                console.warn('[EntityAnim] restoring un-animated properties failed:', err);
            }
            for (const p of removed) entry.props.delete(p);
            if (entry.props.size === 0) this.appliedProps.delete(key);
        }
    }

    /**
     * Get or create a cached pc.Curve with a single [0, value] key.
     * Updates the value in-place if the curve already exists.
     */
    private getOrCreateCurve(key: string, value: number): pc.Curve {
        let curve = this.curveCache.get(key);
        if (curve) {
            curve.keys[0][1] = value;
        } else {
            curve = new pc.Curve([0, value]);
            this.curveCache.set(key, curve);
        }
        return curve;
    }

    /**
     * Get or create a cached pc.CurveSet with three [0, value] curves.
     * Updates the values in-place if the curve set already exists.
     */
    private getOrCreateCurveSet(key: string, x: number, y: number, z: number): pc.CurveSet {
        let curveSet = this.curveSetCache.get(key);
        if (curveSet) {
            curveSet.curves[0].keys[0][1] = x;
            curveSet.curves[1].keys[0][1] = y;
            curveSet.curves[2].keys[0][1] = z;
        } else {
            curveSet = new pc.CurveSet([
                [0, x],
                [0, y],
                [0, z],
            ]);
            this.curveSetCache.set(key, curveSet);
        }
        return curveSet;
    }

    /** The authored particle values (captured on first use, before anything is animated). */
    private particleBase(ps: any): ParticleBase {
        let base = this.particleBases.get(ps);
        if (!base) {
            const copyKeys = (keys: unknown) => (Array.isArray(keys) ? (keys as number[][]).map((k) => [...k]) : null);
            base = {
                rate: ps.rate ?? 0,
                rate2: ps.rate2 ?? ps.rate ?? 0,
                lifetime: ps.lifetime ?? 5,
                alpha: copyKeys(ps.alphaGraph?.keys),
                color: Array.isArray(ps.colorGraph?.curves) ? ps.colorGraph.curves.map((c: { keys: unknown }) => copyKeys(c.keys) ?? [[0, 1]]) : null,
                opacity: 1,
                tint: [1, 1, 1],
            };
            this.particleBases.set(ps, base);
        }
        return base;
    }

    /** Write emitter values that are plain shader uniforms, without the component's reset. */
    private setParticleUniforms(ps: any, v: { rate?: number; rate2?: number; lifetime?: number }): void {
        const emitter = ps.emitter;
        if (!emitter) {
            // Not built yet: the component setters are safe before the emitter exists.
            if (v.rate !== undefined) ps.rate = v.rate;
            if (v.rate2 !== undefined) ps.rate2 = v.rate2;
            if (v.lifetime !== undefined) ps.lifetime = v.lifetime;
            return;
        }
        if (v.rate !== undefined) { ps._rate = v.rate; emitter.rate = v.rate; }
        if (v.rate2 !== undefined) { ps._rate2 = v.rate2; emitter.rate2 = v.rate2; }
        if (v.lifetime !== undefined) { ps._lifetime = v.lifetime; emitter.lifetime = v.lifetime; }
        const material = emitter.material;
        if (material) {
            material.setParameter('rate', emitter.rate);
            material.setParameter('rateDiv', emitter.rate2 - emitter.rate);
            material.setParameter('lifetime', emitter.lifetime);
        }
        // Lifetime also scales the graphs' integration (bounds), so re-quantize them.
        if (v.lifetime !== undefined) emitter.rebuildGraphs?.();
    }

    /** A cached curve whose values are `keys` × `factor`. */
    private scaledCurve(key: string, keys: number[][], factor: number): pc.Curve {
        let curve = this.curveCache.get(key);
        if (!curve || curve.keys.length !== keys.length) {
            curve = new pc.Curve(keys.flatMap(([t, v]) => [t, v * factor]));
            this.curveCache.set(key, curve);
        } else {
            for (let i = 0; i < keys.length; i++) curve.keys[i][1] = keys[i][1] * factor;
        }
        return curve;
    }

    /** A cached curve set whose curve `i` is `curves[i]` × `factors[i]`. */
    private scaledCurveSet(key: string, curves: number[][][], factors: [number, number, number]): pc.CurveSet {
        let set = this.curveSetCache.get(key);
        const sameShape = set && set.curves.length === curves.length && set.curves.every((c, i) => c.keys.length === curves[i].length);
        if (!set || !sameShape) {
            set = new pc.CurveSet(curves.map((keys, i) => keys.flatMap(([t, v]) => [t, v * (factors[i] ?? 1)])));
            this.curveSetCache.set(key, set);
        } else {
            set.curves.forEach((c, i) => { for (let k = 0; k < curves[i].length; k++) c.keys[k][1] = curves[i][k][1] * (factors[i] ?? 1); });
        }
        return set;
    }

    /** Restore the authored particle multipliers (opacity / tint / rate / lifetime) that stopped being animated. */
    private restoreParticle(ps: any, props: string[]): void {
        const base = this.particleBases.get(ps);
        if (!base) return;
        if (props.includes('particle.opacity') && base.alpha) {
            base.opacity = 1;
            ps.alphaGraph = new pc.Curve(base.alpha.flatMap(([t, v]) => [t, v]));
        }
        if (props.some((p) => p.startsWith('particle.tint')) && base.color) {
            base.tint = [1, 1, 1];
            ps.colorGraph = new pc.CurveSet(base.color.map((keys) => keys.flatMap(([t, v]) => [t, v])));
        }
        if (props.includes('particle.rate')) this.setParticleUniforms(ps, { rate: base.rate, rate2: base.rate2 });
        if (props.includes('particle.lifetime')) this.setParticleUniforms(ps, { lifetime: base.lifetime });
    }

    /**
     * Apply animated values to a PlayCanvas entity. Only the animated parts are written.
     * Converts transforms from BabylonJS conventions to PlayCanvas:
     * - Z positions are negated (left-handed → right-handed)
     * - Rotations converted from radians to degrees, Z negated
     */
    private applyValues(entity: pc.Entity, values: Map<string, number>, anim: EntityAnimationConfig): void {
        const animId = anim.id;

        // Apply position values (BabylonJS → PlayCanvas: negate Z)
        if (hasAny(values, TRANSFORM_POSITION)) {
            const pos = entity.getLocalPosition().clone();
            if (values.has('position.x')) pos.x = values.get('position.x')!;
            if (values.has('position.y')) pos.y = values.get('position.y')!;
            if (values.has('position.z')) pos.z = -values.get('position.z')!; // negate Z
            entity.setLocalPosition(pos);
        }

        // Apply rotation values (BabylonJS radians → PlayCanvas degrees, negate Z)
        if (hasAny(values, TRANSFORM_ROTATION)) {
            const rot = readAnimatedEuler(entity, anim.entityType);
            const RAD_TO_DEG = 180 / Math.PI;
            if (values.has('rotation.x')) rot.x = values.get('rotation.x')! * RAD_TO_DEG;
            if (values.has('rotation.y')) rot.y = values.get('rotation.y')! * RAD_TO_DEG;
            if (values.has('rotation.z')) rot.z = -values.get('rotation.z')! * RAD_TO_DEG; // negate Z
            writeAnimatedEuler(entity, anim.entityType, rot);
        }

        // Apply scale values (no conversion needed)
        if (hasAny(values, TRANSFORM_SCALE)) {
            const scale = entity.getLocalScale().clone();
            if (values.has('scale.x')) scale.x = values.get('scale.x')!;
            if (values.has('scale.y')) scale.y = values.get('scale.y')!;
            if (values.has('scale.z')) scale.z = values.get('scale.z')!;
            entity.setLocalScale(scale);
        }

        // Apply particle system properties (no coordinate conversion needed)
        const ps = (entity as any).particlesystem;
        if (ps) {
            // Rate and lifetime go straight to the emitter and its shader uniforms: the
            // component setters rebuild and RESET the emitter (respawning every particle),
            // which would freeze the effect for as long as the animation plays.
            if (values.has('particle.rate')) {
                // PlayCanvas uses seconds-between-births; the animation value is particles/sec.
                // 0 stops emitting. The authored rate → rate2 spread (random interval) is kept.
                const base = this.particleBase(ps);
                const pps = Math.max(0, values.get('particle.rate')!);
                const interval = pps > 0 ? 1 / pps : 1e6;
                const interval2 = base.rate > 0 ? interval * (base.rate2 / base.rate) : interval;
                this.setParticleUniforms(ps, { rate: interval, rate2: interval2 });
            }
            if (values.has('particle.lifetime')) {
                this.particleBase(ps);
                this.setParticleUniforms(ps, { lifetime: Math.max(0.01, values.get('particle.lifetime')!) });
            }
            if (values.has('particle.speed')) {
                const val = values.get('particle.speed')!;
                if (ps.emitterShape === pc.EMITTERSHAPE_SPHERE) {
                    ps.radialSpeedGraph = this.getOrCreateCurve(`${animId}-speed`, val);
                } else {
                    // Box/point emitters use localVelocityGraph — scale all axes uniformly
                    ps.localVelocityGraph = this.getOrCreateCurveSet(`${animId}-localVel`, val, val, val);
                }
            }
            if (values.has('particle.speed2')) {
                const val = values.get('particle.speed2')!;
                if (ps.emitterShape === pc.EMITTERSHAPE_SPHERE) {
                    ps.radialSpeedGraph2 = this.getOrCreateCurve(`${animId}-speed2`, val);
                } else {
                    ps.localVelocityGraph2 = this.getOrCreateCurveSet(`${animId}-localVel2`, val, val, val);
                }
            }
            if (values.has('particle.radialSpeed')) {
                ps.radialSpeedGraph = this.getOrCreateCurve(`${animId}-radialSpeed`, values.get('particle.radialSpeed')!);
            }
            if (values.has('particle.radialSpeed2')) {
                ps.radialSpeedGraph2 = this.getOrCreateCurve(`${animId}-radialSpeed2`, values.get('particle.radialSpeed2')!);
            }
            if (values.has('particle.scale')) {
                ps.scaleGraph = this.getOrCreateCurve(`${animId}-scale`, values.get('particle.scale')!);
            }
            if (values.has('particle.scale2')) {
                ps.scaleGraph2 = this.getOrCreateCurve(`${animId}-scale2`, values.get('particle.scale2')!);
            }
            if (values.has('particle.rotationSpeedMin')) {
                ps.rotationSpeedGraph = this.getOrCreateCurve(`${animId}-rotSpeedMin`, values.get('particle.rotationSpeedMin')!);
            }
            if (values.has('particle.rotationSpeedMax')) {
                ps.rotationSpeedGraph2 = this.getOrCreateCurve(`${animId}-rotSpeedMax`, values.get('particle.rotationSpeedMax')!);
            }
            // Gravity — PlayCanvas uses velocityGraph (CurveSet) for gravity-like behavior
            const gx = values.get('particle.gravity.x');
            const gy = values.get('particle.gravity.y');
            const gz = values.get('particle.gravity.z');
            if (gx !== undefined || gy !== undefined || gz !== undefined) {
                const curGx = ps.velocityGraph?.curves?.[0]?.keys?.[0]?.[1] ?? 0;
                const curGy = ps.velocityGraph?.curves?.[1]?.keys?.[0]?.[1] ?? 0;
                const curGz = ps.velocityGraph?.curves?.[2]?.keys?.[0]?.[1] ?? 0;
                ps.velocityGraph = this.getOrCreateCurveSet(
                    `${animId}-velocity`,
                    gx ?? curGx,
                    gy ?? curGy,
                    gz ?? curGz,
                );
            }
            // Opacity and tint multiply the system's own fade / colour over lifetime, so the
            // particles keep fading out at the end of their life.
            if (values.has('particle.opacity')) {
                const base = this.particleBase(ps);
                base.opacity = clamp01(values.get('particle.opacity')!);
                ps.alphaGraph = this.scaledCurve(`${animId}-opacity`, base.alpha ?? [[0, 1]], base.opacity);
            }
            if (values.has('particle.tint.r') || values.has('particle.tint.g') || values.has('particle.tint.b')) {
                const base = this.particleBase(ps);
                base.tint = [
                    Math.max(0, values.get('particle.tint.r') ?? base.tint[0]),
                    Math.max(0, values.get('particle.tint.g') ?? base.tint[1]),
                    Math.max(0, values.get('particle.tint.b') ?? base.tint[2]),
                ];
                if (base.color) ps.colorGraph = this.scaledCurveSet(`${animId}-tint`, base.color, base.tint);
            }
            // No ps.reset() needed: PlayCanvas graph-property setters call
            // emitter.rebuildGraphs() + resetMaterial(), which re-quantize the
            // curves and re-upload the internal textures. Calling reset() here
            // would respawn every particle each frame the graph is animated.
        }

        // Lights. The component setters forward to the internal light (color is copied), and
        // relighting picks the new values up through its per-frame value comparison.
        const light = (entity as any).light as pc.LightComponent | undefined;
        if (light) {
            const r = values.get('light.color.r');
            const g = values.get('light.color.g');
            const b = values.get('light.color.b');
            if (r !== undefined || g !== undefined || b !== undefined) {
                const current = light.color;
                this.scratchColor.set(
                    Math.max(0, r ?? current.r),
                    Math.max(0, g ?? current.g),
                    Math.max(0, b ?? current.b),
                    1,
                );
                light.color = this.scratchColor;
            }
            if (values.has('light.intensity')) light.intensity = Math.max(0, values.get('light.intensity')!);
            if (values.has('light.range')) light.range = Math.max(0.001, values.get('light.range')!);
            if (values.has('light.coneAngle')) {
                // Keep the inner/outer cone ratio the light was authored with (soft edge width).
                let ratio = this.coneRatios.get(light);
                if (ratio === undefined) {
                    ratio = light.outerConeAngle > 0 ? Math.min(1, light.innerConeAngle / light.outerConeAngle) : 0.8;
                    this.coneRatios.set(light, ratio);
                }
                const outer = Math.min(89.9, Math.max(0.1, values.get('light.coneAngle')!));
                light.outerConeAngle = outer;
                light.innerConeAngle = outer * ratio;
            }
        }

        // Audio emitters: the component volume multiplies every slot's own volume, so it
        // composes with the emitter's configured volume and with the viewer's mute.
        const sound = (entity as any).sound as { volume: number } | undefined;
        if (sound && values.has('audio.volume')) sound.volume = Math.max(0, values.get('audio.volume')!);

        this.presentation.setProperties?.(entity, anim.entityType, anim.entityId, values);

        if (values.has('opacity') && OPACITY_TYPES.has(anim.entityType)) {
            this.presentation.setOpacity?.(entity, anim.entityType, anim.entityId, clamp01(values.get('opacity')!));
        }
        if (values.has('visible')) {
            const visible = values.get('visible')! >= 0.5;
            if (this.presentation.setVisible) this.presentation.setVisible(entity, anim.entityType, anim.entityId, visible);
            else if (entity.enabled !== visible) entity.enabled = visible;
        }
    }

    /** Start playback for all independent animations */
    play(): void {
        for (const anim of this.animations) {
            if (anim.timelineMode !== 'independent' || !anim.enabled) continue;
            // Keep the bulk API behavior identical to playAnimation(id),
            // including rewinding a completed one-shot animation before it
            // resumes. Calling play() on a finished state without rewinding
            // leaves it pinned at its end pose and it immediately stops again.
            this.playAnimation(anim.id);
        }
    }

    /** Pause playback for all independent animations */
    pause(): void {
        for (const state of this.states.values()) {
            state.playing = false;
        }
    }

    /** Reset all independent animations to start */
    reset(): void {
        for (const anim of this.animations) {
            if (anim.timelineMode !== 'independent') continue;
            const state = this.states.get(anim.id);
            if (state) {
                Object.assign(state, initialState(anim), { primed: true });
            }
            if (anim.enabled) this.evaluateAndApply(anim, 0);
        }
    }

    /**
     * The animations an id names: the animation itself, plus the copies one legacy animation was
     * split into (`sourceId` = that id, migrateRegionAnimations), which act as one.
     */
    private idsFor(id: string): string[] {
        return this.animations.filter((a) => a.id === id || a.sourceId === id).map((a) => a.id);
    }

    /** Play a specific animation by ID. Resumes a paused one; restarts a finished 'once' animation. */
    playAnimation(id: string): void {
        for (const one of this.idsFor(id)) this.playOne(one);
    }

    private playOne(id: string): void {
        const state = this.states.get(id);
        if (!state) return;
        const anim = this.animations.find(a => a.id === id);
        if (anim && anim.playbackMode === 'once' && state.time >= anim.duration) {
            state.time = 0;
            state.direction = 1;
            state.delayRemaining = anim.delay || 0;
            state.started = false;
        }
        state.playing = true;
    }

    /** Restart a specific animation from its beginning (honoring its delay) and play it. */
    restartAnimation(id: string): void {
        for (const one of this.idsFor(id)) this.restartOne(one);
    }

    private restartOne(id: string): void {
        const anim = this.animations.find(a => a.id === id);
        const state = this.states.get(id);
        if (!anim || !state || !anim.enabled) return;
        state.time = 0;
        state.direction = 1;
        state.delayRemaining = anim.delay || 0;
        state.started = false;
        state.primed = true;
        state.playing = true;
        this.evaluateAndApply(anim, 0);
    }

    /** Pause a specific animation by ID */
    pauseAnimation(id: string): void {
        for (const one of this.idsFor(id)) {
            const state = this.states.get(one);
            if (state) state.playing = false;
        }
    }

    /** Reset a specific animation by ID */
    resetAnimation(id: string): void {
        for (const one of this.idsFor(id)) {
            const anim = this.animations.find(a => a.id === one);
            const state = this.states.get(one);
            if (state && anim) {
                state.time = 0;
                state.direction = 1;
                state.delayRemaining = anim.delay || 0;
                state.started = false;
                state.primed = true;
                if (anim.enabled) this.evaluateAndApply(anim, 0);
            }
        }
    }

    /**
     * Jump a time-based animation to `time` seconds (clamped to its duration) and apply that
     * pose. Keeps its playing/paused state; skips any remaining delay.
     */
    seekAnimation(id: string, time: number): void {
        for (const one of this.idsFor(id)) {
            const anim = this.animations.find(a => a.id === one);
            const state = this.states.get(one);
            if (!anim || !state || !(anim.duration > 0)) continue;
            state.time = Math.min(anim.duration, Math.max(0, Number.isFinite(time) ? time : 0));
            state.delayRemaining = 0;
            state.primed = true;
            state.started = true;
            if (anim.enabled) this.evaluateAndApply(anim, state.time);
        }
    }

    /** True while a time-based animation is advancing (or waiting out its delay). */
    isAnimationPlaying(id: string): boolean {
        return this.idsFor(id).some((one) => this.states.get(one)?.playing === true);
    }

    /** The tour arrived at waypoint `index`: restart every animation triggered by it. */
    handleWaypointChange(index: number): void {
        if (this.destroyed) return;
        for (const anim of this.animations) {
            if (anim.timelineMode !== 'independent' || !anim.enabled) continue;
            if (hasWaypointTrigger(anim) && anim.triggerWaypoint === index) this.restartAnimation(anim.id);
        }
    }

    /** Evaluate a specific animation at a given time (for editor preview) */
    evaluateAt(animationId: string, time: number): void {
        const anim = this.animations.find(a => a.id === animationId);
        if (!anim) {
            console.warn(`[EntityAnim] evaluateAt: animation "${animationId}" not found in ${this.animations.length} animations`);
            return;
        }
        this.evaluateAndApply(anim, time);
    }

    /**
     * Map a global timeline time to an animation's local time, honoring
     * delay, duration and playbackMode — mirrors what update() produces at
     * that point in real playback so the editor preview matches the
     * published scene.
     */
    private toLocalTime(anim: EntityAnimationConfig, globalTime: number): number {
        const duration = anim.duration;
        if (!(duration > 0)) return 0;
        const t = globalTime - (anim.delay || 0);
        if (t <= 0) return 0;
        switch (anim.playbackMode) {
            case 'loop':
                return t % duration;
            case 'pingpong': {
                const cycle = t % (duration * 2);
                return cycle <= duration ? cycle : duration * 2 - cycle;
            }
            case 'once':
            default:
                return Math.min(t, duration);
        }
    }

    /**
     * Evaluate ALL enabled animations (for editor multi-track preview).
     * @param time - Playhead time in seconds (used for independent animations)
     * @param scrollProgress - Camera tour scroll progress 0-1 (used for scroll-linked animations). If omitted, scroll-linked animations are skipped.
     */
    evaluateAllAt(time: number, scrollProgress?: number): void {
        if (this.destroyed) return;
        for (const anim of this.animations) {
            if (!anim.enabled) continue;
            if (anim.timelineMode === 'scroll') {
                if (scrollProgress !== undefined) {
                    this.evaluateAndApply(anim, scrollProgress * 100);
                }
                // Skip scroll-linked animations when no scrollProgress provided
            } else {
                this.evaluateAndApply(anim, this.toLocalTime(anim, time));
            }
        }
    }

    /** List animation metadata (public API surface for published scenes) */
    listAnimations(): EntityAnimationInfo[] {
        return this.animations.map(a => ({
            id: a.id,
            name: a.name,
            entityId: a.entityId,
            entityType: a.entityType,
            timelineMode: a.timelineMode,
            duration: a.duration,
            playbackMode: a.playbackMode,
            autoplay: a.autoplay,
            ...(hasWaypointTrigger(a) ? { triggerWaypoint: a.triggerWaypoint } : {}),
            playing: this.states.get(a.id)?.playing === true,
        }));
    }

    /** Update the animations list (for editor live updates) */
    setAnimations(animations: EntityAnimationConfig[]): void {
        this.restoreUnanimated(animations);
        this.animations = animations;
        this.warnedEntities.clear();
        this.curveCache.clear();
        this.curveSetCache.clear();
        // Re-initialize states for new animations
        const existingIds = new Set(this.states.keys());
        for (const anim of animations) {
            if (anim.timelineMode === 'independent' && !existingIds.has(anim.id)) {
                this.states.set(anim.id, initialState(anim));
            }
        }
        // Remove states for deleted animations
        const currentIds = new Set(animations.map(a => a.id));
        for (const id of this.states.keys()) {
            if (!currentIds.has(id)) {
                this.states.delete(id);
            }
        }
    }

    /**
     * Every animatable value of a target right now, keyed by property (transforms in BabylonJS
     * conventions, like the keyframes). Used by the editor to capture keyframes.
     */
    readAnimatableValues(entityId: string, entityType: string): Record<string, number> | null {
        const t = this.getEntityTransform(entityId, entityType);
        if (!t) return null;
        if (isSceneAnimationTarget(entityType)) return { ...(t.properties ?? {}) };
        return {
            'position.x': t.position.x, 'position.y': t.position.y, 'position.z': t.position.z,
            'rotation.x': t.rotation.x, 'rotation.y': t.rotation.y, 'rotation.z': t.rotation.z,
            'scale.x': t.scale.x, 'scale.y': t.scale.y, 'scale.z': t.scale.z,
            ...(t.properties ?? {}),
        };
    }

    /**
     * Get the current transform of an entity in BabylonJS conventions, plus its other
     * animatable properties (`properties`; `particleProps` is the same object, kept for older
     * editor code). Used by the editor to capture keyframes from the scene.
     */
    getEntityTransform(entityId: string, entityType: string): { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { x: number; y: number; z: number }; properties?: Record<string, number>; particleProps?: Record<string, number> } | null {
        // Scene-wide targets expose scalar properties via sceneHandlers.readValues()
        if (isSceneAnimationTarget(entityType)) {
            const handler = this.sceneHandlers[entityType];
            const vals = handler?.readValues(entityId);
            if (!vals) return null;
            return {
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 1, y: 1, z: 1 },
                properties: vals,
                particleProps: vals,
            };
        }
        const entity = this.entityLookup(entityId, entityType);
        if (!entity) return null;

        const pos = entity.getLocalPosition();
        const rot = readAnimatedEuler(entity, entityType);
        // A "keep same size on screen" hotspot carries a camera-distance multiplier in its scale;
        // keyframes record its set size.
        const screenSizeBase = (entity as pc.Entity & { screenSizeState?: { base: [number, number, number] } }).screenSizeState?.base;
        const scale = screenSizeBase
            ? { x: screenSizeBase[0], y: screenSizeBase[1], z: screenSizeBase[2] }
            : entity.getLocalScale();

        // Convert PlayCanvas → BabylonJS conventions (negate Z, degrees→radians)
        const DEG_TO_RAD = Math.PI / 180;
        const props: Record<string, number> = {};
        const result: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { x: number; y: number; z: number }; properties?: Record<string, number>; particleProps?: Record<string, number> } = {
            position: { x: pos.x, y: pos.y, z: -pos.z },
            rotation: { x: rot.x * DEG_TO_RAD, y: rot.y * DEG_TO_RAD, z: -rot.z * DEG_TO_RAD },
            scale: { x: scale.x, y: scale.y, z: scale.z },
        };

        // Include particle system properties if available
        const ps = (entity as any).particlesystem;
        if (ps) {
            Object.assign(props, {
                'particle.rate': ps.rate > 0 ? 1 / ps.rate : 0, // PC rate is seconds-between-births → particles/sec
                'particle.lifetime': ps.lifetime ?? 5,
                'particle.speed': ps.emitterShape === pc.EMITTERSHAPE_SPHERE
                    ? (ps.radialSpeedGraph?.keys?.[0]?.[1] ?? 1)
                    : (ps.localVelocityGraph?.curves?.[0]?.keys?.[0]?.[1] ?? 1),
                'particle.speed2': ps.emitterShape === pc.EMITTERSHAPE_SPHERE
                    ? (ps.radialSpeedGraph2?.keys?.[0]?.[1] ?? 1)
                    : (ps.localVelocityGraph2?.curves?.[0]?.keys?.[0]?.[1] ?? 1),
                'particle.radialSpeed': ps.radialSpeedGraph?.keys?.[0]?.[1] ?? 0,
                'particle.radialSpeed2': ps.radialSpeedGraph2?.keys?.[0]?.[1] ?? 0,
                'particle.rotationSpeedMin': ps.rotationSpeedGraph?.keys?.[0]?.[1] ?? 0,
                'particle.rotationSpeedMax': ps.rotationSpeedGraph2?.keys?.[0]?.[1] ?? 0,
                'particle.scale': ps.scaleGraph?.keys?.[0]?.[1] ?? 1,
                'particle.scale2': ps.scaleGraph2?.keys?.[0]?.[1] ?? 1,
                'particle.opacity': this.particleBases.get(ps)?.opacity ?? 1,
                'particle.tint.r': this.particleBases.get(ps)?.tint[0] ?? 1,
                'particle.tint.g': this.particleBases.get(ps)?.tint[1] ?? 1,
                'particle.tint.b': this.particleBases.get(ps)?.tint[2] ?? 1,
            });
            if (ps.velocityGraph) {
                props['particle.gravity.x'] = ps.velocityGraph.curves?.[0]?.keys?.[0]?.[1] ?? 0;
                props['particle.gravity.y'] = ps.velocityGraph.curves?.[1]?.keys?.[0]?.[1] ?? 0;
                props['particle.gravity.z'] = ps.velocityGraph.curves?.[2]?.keys?.[0]?.[1] ?? 0;
            }
        }

        const light = (entity as any).light as pc.LightComponent | undefined;
        if (light) {
            const c = light.color;
            props['light.color.r'] = c.r;
            props['light.color.g'] = c.g;
            props['light.color.b'] = c.b;
            props['light.intensity'] = light.intensity;
            props['light.range'] = light.range;
            props['light.coneAngle'] = light.outerConeAngle;
        }

        const sound = (entity as any).sound as { volume: number } | undefined;
        if (sound && typeof sound.volume === 'number') props['audio.volume'] = sound.volume;

        const own = this.presentation.getProperties?.(entity, entityType, entityId);
        if (own) Object.assign(props, own);

        if (OPACITY_TYPES.has(entityType)) {
            props['opacity'] = this.presentation.getOpacity?.(entity, entityType, entityId) ?? 1;
        }
        const visible = this.presentation.getVisible?.(entity, entityType, entityId) ?? (entity.enabled !== false);
        props['visible'] = visible ? 1 : 0;

        result.properties = props;
        result.particleProps = props;
        return result;
    }

    destroy(): void {
        this.destroyed = true;
        this.appliedProps.clear();
        this.states.clear();
        this.animations = [];
        this.warnedEntities.clear();
        this.curveCache.clear();
        this.curveSetCache.clear();
    }
}
